import { AURA_CONFIG } from '../components/systems/AuraSystem';

/**
 * Hilfsfunktionen für die Berechnung von Aura-Leveln und -Punkten.
 *
 * Die Schwellen sind ein Parameter: Jede Schule kann eigene Level-Grenzen
 * haben (GET /api/v1/aura/config). Ohne Angabe gelten die Standardwerte -
 * bis September 2026 rechneten Konto-Seite und Profilkarten IMMER mit den
 * Standardwerten, egal was die Schule eingestellt hatte.
 */

const normalizePoints = (points, label = 'Aura-Punkte') => {
  if (typeof points !== 'number' || Number.isNaN(points) || points < 0) {
    if (process.env.NODE_ENV !== 'test') {
      console.warn(`Ungültiger Wert für ${label}:`, points);
    }
    return 0;
  }
  return points;
};

const normalizeThresholds = (thresholds) => {
  const source = thresholds && typeof thresholds === 'object' ? thresholds : AURA_CONFIG.levelThresholds;
  const levels = Object.keys(source)
    .map(Number)
    .filter(level => Number.isFinite(level) && Number.isFinite(Number(source[level])))
    .sort((a, b) => a - b);
  if (levels.length === 0) return normalizeThresholds(AURA_CONFIG.levelThresholds);
  return { levels, at: level => Number(source[level]) };
};

/**
 * Berechnet das Aura-Level basierend auf den Punkten (Threshold-basiert)
 * @param {number} points - Aura-Punkte
 * @param {Object} [thresholds] - Level-Schwellen der Schule ({ 1: 0, 2: 500, ... })
 * @returns {number} - Das berechnete Level (1-5)
 */
export const getAuraLevel = (points = 0, thresholds) => {
  const safePoints = normalizePoints(points);
  const { levels, at } = normalizeThresholds(thresholds);

  let level = levels[0] || 1;
  for (let i = levels.length - 1; i >= 0; i -= 1) {
    if (safePoints >= at(levels[i])) {
      level = levels[i];
      break;
    }
  }
  return level;
};

// Hinweis: Die frühere `calculateAuraForSession()` ist entfallen. Sie bildete die
// Backend-Formel inklusive Fokus-Tiers nach, wurde aber nirgends produktiv aufgerufen
// (nur in ihren eigenen Tests). Aura wird ausschliesslich im Backend berechnet und
// kommt als `auraEarned` mit den Session-Daten zurueck.

/**
 * Berechnet detaillierte Level-Progress-Informationen (Threshold-basiert)
 *
 * Der Fortschritt bezieht sich IMMER auf die Spanne des aktuellen Levels
 * (z.B. Level 4 = 2000 bis 5000), nicht auf "Punkte geteilt durch naechste
 * Schwelle". 4356 Aura sind also (4356 - 2000) / (5000 - 2000) = 78 %, nicht
 * 4356 / 5000 = 87 %. Damit die Zahl auf dem Bildschirm nachvollziehbar ist,
 * liefert die Funktion beide Schwellen mit - die Anzeigen schreiben sie unter
 * den Balken.
 *
 * Gerundet wird ab: "100 %" steht erst, wenn das naechste Level wirklich
 * erreicht ist (vorher stand bei 999 Punkten "100 % zu Level 3" neben
 * "noch 1 Aura").
 *
 * @param {number} points - Aktuelle Punkte
 * @param {Object} [thresholds] - Level-Schwellen der Schule
 * @returns {Object} - Progress-Informationen
 */
export const getAuraLevelProgress = (points = 0, thresholds) => {
  const safePoints = normalizePoints(points);
  const { levels, at } = normalizeThresholds(thresholds);
  const currentLevel = getAuraLevel(safePoints, thresholds);
  const currentThreshold = at(currentLevel);
  const nextLevel = levels.find(level => level > currentLevel);

  // Max Level erreicht
  if (!nextLevel) {
    return {
      currentLevel,
      nextLevel: null,
      currentThreshold,
      nextThreshold: null,
      pointsInLevel: safePoints - currentThreshold,
      pointsToNextLevel: 0,
      progressPercentage: 100,
      isMaxLevel: true,
    };
  }

  const nextThreshold = at(nextLevel);
  const pointsInLevel = safePoints - currentThreshold;
  const levelRange = Math.max(1, nextThreshold - currentThreshold);
  const pointsToNextLevel = nextThreshold - safePoints;
  const progressPercentage = Math.max(0, Math.min(100, Math.floor((pointsInLevel / levelRange) * 100)));

  return {
    currentLevel,
    nextLevel,
    currentThreshold,
    nextThreshold,
    pointsInLevel,
    pointsToNextLevel,
    progressPercentage,
    isMaxLevel: false,
  };
};
